/**
 * In-app, in-process event bus.
 *
 * Modules publish typed events; other modules subscribe to those types via
 * their `Module.events.subscribesTo` declarations. This is the runtime side
 * of cross-module integration in the browser.
 *
 * The persistent counterpart lives in Postgres (`core.events_outbox`) and is
 * emitted from triggers / Edge Functions. Use this in-app bus for UI
 * reactions (toasts, refetch, optimistic updates); use the Postgres outbox
 * for things that must survive a tab refresh.
 *
 * Event type registry: a module declares its events on `AppEventMap` via
 * declaration merging. Subscribing to an unknown event is a TS error.
 *
 * Example (in packages/modules/clients/src/events.ts):
 *
 *   declare module '@pc/core' {
 *     interface AppEventMap {
 *       'client.created': { id: string; displayName: string };
 *       'client.updated': { id: string };
 *     }
 *   }
 */

// Modules augment this interface to register events. It is intentionally
// empty here so that without any modules loaded, emitting an unknown event
// is a compile error rather than a permissive `unknown`.
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export interface AppEventMap {}

export type EventName = keyof AppEventMap & string;
export type EventPayload<E extends EventName> = AppEventMap[E];

export type EventHandler<E extends EventName> = (payload: EventPayload<E>) => void;

export interface EventBus {
  emit<E extends EventName>(event: E, payload: EventPayload<E>): void;
  on<E extends EventName>(event: E, handler: EventHandler<E>): () => void;
  onAny(handler: (event: string, payload: unknown) => void): () => void;
}

export function createEventBus(): EventBus {
  const handlers = new Map<string, Set<(p: unknown) => void>>();
  const wildcards = new Set<(event: string, p: unknown) => void>();

  return {
    emit(event, payload) {
      const set = handlers.get(event);
      if (set) {
        for (const h of set) {
          try {
            h(payload);
          } catch (err) {
            // Never let one bad subscriber stop others.
            console.error(`[event-bus] handler for ${event} threw`, err);
          }
        }
      }
      for (const w of wildcards) {
        try {
          w(event, payload);
        } catch (err) {
          console.error('[event-bus] wildcard handler threw', err);
        }
      }
    },
    on(event, handler) {
      let set = handlers.get(event);
      if (!set) {
        set = new Set();
        handlers.set(event, set);
      }
      const h = handler as (p: unknown) => void;
      set.add(h);
      return () => {
        set?.delete(h);
      };
    },
    onAny(handler) {
      wildcards.add(handler);
      return () => {
        wildcards.delete(handler);
      };
    },
  };
}

// Singleton bus used by the running app. Tests may construct their own.
let bus: EventBus | null = null;

export function appBus(): EventBus {
  if (!bus) bus = createEventBus();
  return bus;
}

export function resetEventBusForTests(): void {
  bus = createEventBus();
}
